import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchImmobileDetail, startAstemaster, fetchOmiReports, deleteOmiReport } from '../lib/api';
import OmiReportModal from '../components/OmiReportModal';

export default function ImmobileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [immobile, setImmobile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [reportAccess, setReportAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [omiReports, setOmiReports] = useState([]);
  const [showOmiModal, setShowOmiModal] = useState(false);

  const loadOmi = () => fetchOmiReports(id).then(setOmiReports).catch(() => {});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchImmobileDetail(id)
      .then((res) => { if (alive) { setImmobile(res.immobile); setLeads(res.leads); setReportAccess(res.reportAccess); setErr(null); } })
      .catch((e) => { if (alive) setErr(e?.response?.data?.error || e?.message || 'Errore'); })
      .finally(() => { if (alive) setLoading(false); });
    loadOmi();
    return () => { alive = false; };
  }, [id]);

  if (loading) return <main className="content"><p style={muted}>Caricamento…</p></main>;
  if (err) return <main className="content"><p style={muted}>{err}</p><Link to="/app/immobili">← Immobili</Link></main>;
  if (!immobile) return null;

  const eur = (v) => v != null ? '€ ' + Number(v).toLocaleString('it-IT') : '—';
  const dt = (v) => v ? new Date(v).toLocaleDateString('it-IT') : '—';

  return (
    <main className="content">
      <div className="page__header">
        <div className="page__title-wrap">
          <button className="btn btn--ghost" onClick={() => navigate('/app/immobili')} style={{ marginBottom: 8 }}>← Immobili</button>
          <h1 className="page__title">{immobile.comune} — {immobile.indirizzo}</h1>
          <p className="page__sub">{immobile.tipologia || immobile.categoria_catastale} · {immobile.tribunale}</p>
        </div>
        <div className="page__actions">
          {immobile.url_report && (
            <a className="btn btn--solid" href={immobile.url_report} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              📊 Apri analisi
            </a>
          )}
          {immobile.url_report && immobile.url_pvp && (
            <button
              type="button"
              className={immobile.needs_reanalysis ? 'btn btn--solid' : 'btn'}
              title={immobile.needs_reanalysis ? "PVP aggiornato dopo l'analisi — consigliato ripetere" : "Rilancia l'analisi con dati aggiornati"}
              onClick={async () => {
                const ok = window.confirm(`Ripetere l'analisi?\nIl report precedente verrà sovrascritto.`);
                if (!ok) return;
                try {
                  const res = await startAstemaster(immobile.id, { force: true });
                  setImmobile({ ...immobile, url_report: null, needs_reanalysis: false, job_status: res.status || 'queued' });
                } catch (e) {
                  window.alert("Impossibile ripetere l'analisi: " + (e?.response?.data?.message || e?.message || 'errore'));
                }
              }}>
              ↻ Ripeti analisi{immobile.needs_reanalysis ? ' ⚠' : ''}
            </button>
          )}
          {reportAccess && !reportAccess.blocked && (
            <span style={{ fontSize: 12, color: 'var(--fg-muted)', alignSelf: 'center' }}>
              {reportAccess.viewed}/{reportAccess.limit} analisi viste
            </span>
          )}
        </div>
      </div>

      {reportAccess?.blocked && (
        <section style={{ ...card, background: 'oklch(0.97 0.04 70)', border: '1px solid oklch(0.80 0.13 70)', textAlign: 'center', padding: 28 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Hai esaurito le analisi gratuite</h2>
          <p style={{ margin: '0 0 16px', color: 'var(--fg-muted)', fontSize: 14, lineHeight: 1.5 }}>
            Per continuare a vedere le analisi degli immobili, contatta {reportAccess.contact_org || 'lo studio'}.
          </p>
          {reportAccess.contact_phone && (
            <a
              href={`https://wa.me/${reportAccess.contact_phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Ciao, sono interessato/a a continuare a vedere le analisi degli immobili in asta. Mi date una mano?`)}`}
              target="_blank" rel="noreferrer"
              className="btn btn--solid"
              style={{ textDecoration: 'none', background: '#25D366', borderColor: '#25D366' }}>
              💬 Scrivi su WhatsApp
            </a>
          )}
        </section>
      )}

      {/* Sintesi immobile */}
      <section style={card}>
        <div style={grid}>
          <Field label="Piano" value={immobile.piano || '—'} />
          <Field label="Superficie" value={immobile.superficie_mq ? `${immobile.superficie_mq} mq` : '—'} />
          <Field label="Camere / Bagni" value={`${immobile.n_camere ?? '—'} / ${immobile.n_bagni ?? '—'}`} />
          <Field label="Base asta" value={eur(immobile.prezzo_base)} />
          <Field label="Offerta minima" value={eur(immobile.offerta_minima)} />
          <Field label="Data asta" value={dt(immobile.data_vendita)} />
          <Field label="Procedura" value={`${immobile.n_procedura || ''}/${immobile.anno_procedura || ''}`} />
          <Field label="Verdetto" value={immobile.rivelasta_verdetto || '—'} />
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
          {immobile.url_pvp && <a href={immobile.url_pvp} target="_blank" rel="noreferrer" style={linkSm}>Scheda PVP ↗</a>}
          {immobile.url_perizia && <a href={immobile.url_perizia} target="_blank" rel="noreferrer" style={linkSm}>Perizia ↗</a>}
        </div>
      </section>

      {/* Lead dell'immobile */}
      <section style={{ ...card, marginTop: 16 }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={cardTitle}>Lead ricevuti <span style={badge}>{leads.length}</span></div>
        </header>

        {leads.length === 0 ? (
          <p style={muted}>Nessun lead per questo immobile (org corrente). I lead compaiono qui man mano che arrivano da Getrix.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {leads.map((l) => {
              const c = l.customer || {};
              const others = (c.interests_count || 1) - 1;
              return (
                <div key={l.id} style={leadRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{c.customer_name || 'Senza nome'}</div>
                    <div style={{ fontSize: 12, color: 'oklch(0.5 0 0)' }}>
                      {c.customer_email || '—'}{c.customer_phone ? ` · ${c.customer_phone}` : ''}
                    </div>
                    {others > 0 && (
                      <div style={{ fontSize: 11, color: 'oklch(0.55 0.1 250)' }}>
                        anche su altri {others} immobil{others === 1 ? 'e' : 'i'}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'oklch(0.45 0 0)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div>{l.total_activities} attività</div>
                    <div>{dt(l.last_activity_at)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {c.customer_phone && <a className="btn btn--ghost" href={`tel:${c.customer_phone}`} style={linkBtn}>📞</a>}
                    {c.customer_email && <a className="btn btn--ghost" href={`mailto:${c.customer_email}`} style={linkBtn}>✉️</a>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Storico Report OMI (compravendite dichiarate AdE) */}
      <section style={{ ...card, marginTop: 16 }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={cardTitle}>
            Report OMI <span style={badge}>{omiReports.length}</span>
          </div>
          <button
            onClick={() => setShowOmiModal(true)}
            style={{ padding: '6px 12px', border: '1px solid oklch(0.4 0.13 250)', background: 'white', color: 'oklch(0.4 0.13 250)', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
          >
            + Nuovo report OMI
          </button>
        </header>
        {omiReports.length === 0 ? (
          <p style={muted}>Nessun report generato per questo immobile.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {omiReports.map((r) => (
              <div key={r.id} style={leadRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    Raggio {r.raggio}m · {r.anno_da}{r.anno_a !== r.anno_da ? `–${r.anno_a}` : ''}
                    {r.tipologia ? ` · ${r.tipologia}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: 'oklch(0.5 0 0)' }}>
                    {r.atti ?? 0} atti · {r.immobili ?? 0} immobili · {eur(r.valore_eur)}
                    {r.user ? ` · ${r.user}` : ''} · {dt(r.created_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <a href={r.url} target="_blank" rel="noopener" style={{ ...linkBtn, color: 'oklch(0.4 0.13 250)', border: '1px solid oklch(0.85 0 0)', borderRadius: 6 }}>
                    Apri
                  </a>
                  <button
                    onClick={async () => {
                      if (!confirm('Rimuovere questo report dallo storico?')) return;
                      await deleteOmiReport(r.id);
                      loadOmi();
                    }}
                    style={{ ...linkBtn, color: 'oklch(0.5 0.15 25)', border: '1px solid oklch(0.85 0 0)', borderRadius: 6, background: 'white', cursor: 'pointer' }}
                  >
                    Elimina
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {showOmiModal && (
        <OmiReportModal
          row={immobile}
          onClose={() => { setShowOmiModal(false); loadOmi(); }}
        />
      )}
    </main>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'oklch(0.5 0 0)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

const card = { background: 'white', border: '1px solid oklch(0.92 0 0)', borderRadius: 12, padding: 20, maxWidth: 860 };
const cardTitle = { fontSize: 16, fontWeight: 600 };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 };
const muted = { color: 'oklch(0.55 0 0)', fontSize: 13 };
const linkSm = { fontSize: 12.5, color: 'oklch(0.45 0.13 250)', textDecoration: 'none' };
const badge = { display: 'inline-block', background: 'oklch(0.93 0.05 250)', color: 'oklch(0.4 0.13 250)', borderRadius: 100, padding: '1px 9px', fontSize: 12, marginLeft: 6 };
const leadRow = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid oklch(0.94 0 0)', borderRadius: 10 };
const linkBtn = { textDecoration: 'none', padding: '4px 8px' };
